import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import "./ServicesTab.css";

// Captain-or-above. Live inventory of every backend service in the repo —
// what it does, how it deploys, what its auth looks like. Source of truth
// is backend/services/graphql/services-manifest.js; the markdown twin is
// SERVICES.md at the repo root.
//
// Why a separate tab and not just SERVICES.md? Two reasons:
//   1. Phone-friendly — the markdown reads OK in a code viewer but the
//      table here filters/sorts and exposes the same info without leaving
//      the admin UI.
//   2. The data is queried live, so an out-of-date markdown twin can't
//      mislead — the manifest is what the Lambda ships with.

const GRAPHQL_URL =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_GRAPHQL_API_URL) ||
  "https://nkeefgsaz2.execute-api.us-east-1.amazonaws.com/prod/graphql";

const SERVICES_QUERY = `
  query Services {
    services {
      id
      name
      category
      type
      stack
      folder
      functionName
      deploysVia
      purpose
      auth
      notes
      status
      tags
    }
  }
`;

// Filter chips ordered loosely by importance.
const CATEGORY_LABELS = {
  api: "API",
  background: "Background",
  trigger: "Trigger",
  webhook: "Webhook",
  specialized: "Specialized",
};

const STATUS_TINT = {
  verified: "ok",
  partial: "warn",
  unverified: "muted",
};

const ServicesTab = () => {
  const { getAuthHeaders } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };
      const resp = await fetch(GRAPHQL_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({ query: SERVICES_QUERY }),
      });
      const body = await resp.json();
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      if (body.errors && body.errors.length) {
        throw new Error(body.errors.map((e) => e.message).join("; "));
      }
      setServices(body.data?.services || []);
    } catch (err) {
      setError(err.message || "Failed to load services");
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const categories = useMemo(() => {
    const seen = new Set();
    for (const s of services) seen.add(s.category);
    return ["all", ...[...seen].sort()];
  }, [services]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return services.filter((s) => {
      if (filter !== "all" && s.category !== filter) return false;
      if (!q) return true;
      const haystack = [
        s.name,
        s.purpose,
        s.folder,
        s.functionName || "",
        s.stack || "",
        s.deploysVia,
        s.auth,
        (s.tags || []).join(" "),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [services, filter, search]);

  return (
    <div className="services-tab">
      <h2 className="services-tab__title">Services</h2>

      <div className="services-tab__hint">
        Live inventory from <code>backend/services/graphql/services-manifest.js</code>.
        Markdown twin: <code>SERVICES.md</code>. Edit both when a service
        changes.
      </div>

      {error && <div className="services-tab__error">Error: {error}</div>}

      <div className="services-tab__controls">
        <input
          type="search"
          placeholder="Search name, purpose, tags…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="services-tab__search"
        />
        <button
          type="button"
          className="services-tab__btn"
          onClick={load}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="services-tab__filters">
        {categories.map((c) => (
          <button
            key={c}
            type="button"
            className={
              c === filter
                ? "services-tab__chip services-tab__chip--active"
                : "services-tab__chip"
            }
            onClick={() => setFilter(c)}
          >
            {c === "all" ? "All" : CATEGORY_LABELS[c] || c}
          </button>
        ))}
      </div>

      <div className="services-tab__meta">
        {filtered.length} of {services.length} shown
      </div>

      {!loading && filtered.length === 0 && (
        <div className="services-tab__info">No services match this filter.</div>
      )}

      {filtered.map((svc) => (
        <details key={svc.id} className="services-tab__card">
          <summary className="services-tab__summary">
            <span className="services-tab__name">{svc.name}</span>
            <span
              className={`services-tab__status services-tab__status--${
                STATUS_TINT[svc.status] || "muted"
              }`}
            >
              {svc.status}
            </span>
            <span className="services-tab__category">
              {CATEGORY_LABELS[svc.category] || svc.category}
            </span>
          </summary>
          <div className="services-tab__body">
            <div className="services-tab__row">
              <span className="services-tab__label">Type</span>
              <span>{svc.type}</span>
            </div>
            {svc.stack && (
              <div className="services-tab__row">
                <span className="services-tab__label">Stack</span>
                <code>{svc.stack}</code>
              </div>
            )}
            {svc.functionName && (
              <div className="services-tab__row">
                <span className="services-tab__label">Function</span>
                <code>{svc.functionName}</code>
              </div>
            )}
            <div className="services-tab__row">
              <span className="services-tab__label">Folder</span>
              <code>{svc.folder}</code>
            </div>
            <div className="services-tab__row">
              <span className="services-tab__label">Deploys via</span>
              <code>{svc.deploysVia}</code>
            </div>
            <div className="services-tab__row">
              <span className="services-tab__label">Purpose</span>
              <span>{svc.purpose}</span>
            </div>
            <div className="services-tab__row">
              <span className="services-tab__label">Auth</span>
              <span>{svc.auth}</span>
            </div>
            {svc.notes && (
              <div className="services-tab__row">
                <span className="services-tab__label">Notes</span>
                <span>{svc.notes}</span>
              </div>
            )}
            {svc.tags && svc.tags.length > 0 && (
              <div className="services-tab__tags">
                {svc.tags.map((t) => (
                  <span key={t} className="services-tab__tag">
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </details>
      ))}
    </div>
  );
};

export default ServicesTab;
